import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { useNavigate } from "react-router";
import C from "classnames";

import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { useUserExams } from "sj-ui-toolkit/src/api/userInitiatedExams";

import { generateEstimatedTime } from "../../../../helpers/assessments";
import { AssessmentCard } from "../DashboardCards/AssessmentCard";
import S from "./AssessmentSection.module.scss";

const PROFICIENCY_THRESHOLDS = { GROWTH_OPPORTUNITIES: 60, PROFICIENCIES: 80 };
const PROFICIENCY_COLORS = {
  foundational: "var(--indigo-dataviz-1)",
  intermediate: "var(--indigo-dataviz-2)",
  advanced: "var(--indigo-dataviz-3)",
};

const getProficiencyLabel = score => {
  if (score == null) return null;
  if (score >= PROFICIENCY_THRESHOLDS.PROFICIENCIES) return "Advanced";
  if (score >= PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES) return "Intermediate";
  return "Foundational";
};

const getProficiencyColor = score => {
  if (score == null) return "var(--surface-600)";
  if (score >= PROFICIENCY_THRESHOLDS.PROFICIENCIES) return PROFICIENCY_COLORS.advanced;
  if (score >= PROFICIENCY_THRESHOLDS.GROWTH_OPPORTUNITIES) return PROFICIENCY_COLORS.intermediate;
  return PROFICIENCY_COLORS.foundational;
};

const formatCompletedDate = dateString => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

export const AssessmentSection = () => {
  const navigate = useNavigate();
  const { navigateTo } = useSmartNavigation();

  const { data, isLoading } = useUserExams({
    includeStatuses: ["started", "completed"],
  });

  if (isLoading || !data) {
    return (
      <div className="mt-8">
        <h2 className="atlas-h5 pb-4">Assessments</h2>
        <Skeleton width="100%" height="400px" />
      </div>
    );
  }

  const { activeExam, sections } = data;

  const activeExamCard = activeExam
    ? {
        examInstanceId: activeExam.exam.examInstanceId,
        section: activeExam.section,
        title: activeExam.section.title,
        description: activeExam.section.description,
        estimatedTime: generateEstimatedTime(
          activeExam.exam.examConfig?.filters?.topicPrefixes ?? []
        ),
      }
    : null;

  const navigateToAssessments = () => {
    navigate(buildRoute("Learn.routes.assessments.route"));
  };

  const renderProficiencies = () => (
    <div className={S.proficienciesColumn}>
      <span className={S.proficienciesHeading}>Proficiencies</span>
      {sections.map(({ section, latestScorePercent, latestScoreCompletedAt }) => {
        const title = section.title ?? "";
        const hasTaken = latestScorePercent != null;
        const scoreRounded = hasTaken ? Math.round(latestScorePercent) : 0;
        const latestCompletedAt = latestScoreCompletedAt;

        return (
          <div key={section.path} className={S.proficiencyRow}>
            <ProgressBar
              value={hasTaken ? scoreRounded : 0}
              showValue={false}
              color={hasTaken ? getProficiencyColor(latestScorePercent) : undefined}
              className={S.progressBar}
            />
            <span>{hasTaken ? `${scoreRounded}%` : "\u2014"}</span>
            <div>
              <div className="mb-1">{title}</div>
              {hasTaken ? (
                <div className="text-muted body-sm">
                  {getProficiencyLabel(latestScorePercent)}
                  {latestCompletedAt && (
                    <span className="ml-3">{formatCompletedDate(latestCompletedAt)}</span>
                  )}
                </div>
              ) : (
                <div className="text-muted body-sm">Not taken</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="mt-8">
      <h2 className="atlas-h5 pb-4">Assessments</h2>
      <div className={S.cardWrapper}>
        <div className={S.cardContainer}>
          <div className={S.detailsColumn}>
            <h3 className={C("atlas-h1-alt", S.heading)}>Assess your knowledge and progress</h3>
            <p className="body-md">
              Assess your proficiency in a range of topics. Retake assessments to track your
              progress over time and get recommended lessons and paths tailored for you.
            </p>
            <div className="pb-3">
              <Button label="View assessments" onClick={navigateToAssessments} />
            </div>
          </div>
          {activeExamCard ? (
            <div className={S.examColumn}>
              <div className="flex flex-wrap gap-3">
                <AssessmentCard
                  key={activeExamCard.examInstanceId}
                  size="small"
                  type="Assessment"
                  title={activeExamCard.title}
                  description={activeExamCard.description}
                  estimatedTime={activeExamCard.estimatedTime}
                  status="InProgress"
                  onNavigate={() =>
                    navigateTo(
                      buildRoute("Ka.routes.userInitiatedExam.route", {
                        section: activeExamCard.section.path,
                      })
                    )
                  }
                />
              </div>
            </div>
          ) : (
            renderProficiencies()
          )}
        </div>
      </div>
    </div>
  );
};
